import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { ActionButton, ActionInput } from '../parts/ActionButton.js';
import { emojiData, emojiGroups } from './emoji-data.js';
import './chats.css';

/** Shared with message reactions. Names and groups come from the bundled Unicode data. */
export function EmojiPicker({ onPick, onClose }: { onPick: (emoji: string) => void; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState(0);
  const grid = useRef<HTMLDivElement>(null);
  const shown = useMemo(() => emojiData.filter(([, name, g]) => query.trim()
    ? query.toLowerCase().trim().split(/\s+/).every(word => name.toLowerCase().includes(word)) : g === group), [query, group]);
  const move = (event: KeyboardEvent, index: number) => {
    const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 8, ArrowUp: -8 }[event.key];
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? shown.length - 1 : step === undefined ? null : Math.max(0, Math.min(shown.length - 1, index + step));
    if (next === null) return;
    event.preventDefault();
    grid.current?.querySelectorAll<HTMLButtonElement>('button')[next]?.focus();
  };
  return <div className="ch-emoji-picker" role="dialog" aria-label="Emoji" onKeyDown={e => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
  }}>
    <ActionInput autoFocus type="search" aria-label="Search" placeholder="Search" value={query} onChange={e => setQuery(e.target.value)}
      onKeyDown={e => { if (e.key === 'ArrowDown') { e.preventDefault(); grid.current?.querySelector('button')?.focus(); } }} />
    <div className="ch-emoji-groups" role="group" aria-label="Emoji">
      {emojiGroups.map((name, i) => <ActionButton key={name} type="button" aria-pressed={!query && group === i}
        onClick={() => { setGroup(i); setQuery(''); }}>{name}</ActionButton>)}
    </div>
    <div ref={grid} className="ch-emoji-grid">
      {shown.map(([emoji, name], i) => <ActionButton key={emoji} type="button" aria-label={name} title={name}
        onKeyDown={e => move(e, i)} onClick={() => onPick(emoji)}>{emoji}</ActionButton>)}
    </div>
  </div>;
}
